import {isDetachedElement} from '@malt/dom';
import {init, destroyModulesFromEl} from '../control';

const TAG_IGNORE_LIST = new Set([
    // Generic elements that never have data-js attributes
    'svg',
    'script',
    'link',

    // Attributes related to SVG elements
    'g',
    'circle',
    'path',
    'text',
    'rect',
    'tspan',
    'line',
    'use',
]);

function shouldBeIgnored(node) {
    const tagName = (node.tagName || '').toLowerCase();
    return !tagName || TAG_IGNORE_LIST.has(tagName);
}

function handleAddedNode(node) {
    if (shouldBeIgnored(node)) {
        return;
    }

    // Already initialize, no need to go down in the graph or to initialize something.
    if (node.hop) {
        return;
    }

    // Do not try to initialize element if it not in the DOM anymore.
    if (isDetachedElement(node)) {
        return;
    }

    init(node);
}

function handleRemovedNode(node) {
    if (shouldBeIgnored(node)) {
        return;
    }

    // Already done, no need to go down in the graph or to destroy something.
    if (node.hop === null) {
        return;
    }

    // Do not try to destroy element if it has been removed from the DOM.
    if (!isDetachedElement(node)) {
        return;
    }

    // Go down in the DOM Tree and destroy all child.
    node.childNodes.forEach((childNode) => handleRemovedNode(childNode));

    // Then, destroy current node.
    destroyModulesFromEl(node);
}

function handleMutation(mutation) {
    const removedNodes = mutation.removedNodes;
    if (removedNodes) {
        removedNodes.forEach((node) => handleRemovedNode(node));
    }

    const addedNodes = mutation.addedNodes;
    if (addedNodes) {
        addedNodes.forEach((node) => handleAddedNode(node));
    }
}

function observerCallback(mutations) {
    mutations.forEach((mutation) => handleMutation(mutation));
}

function startObserver() {
    const MutationObserverImpl = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
    if (!MutationObserverImpl) {
        console.warn('It looks like mutation observer is not supported by the current environment.');
        return;
    }

    if (!document) {
        throw new Error('Mutation observer cannot be started in a non DOM environment.');
    }

    const mutationObserver = new MutationObserverImpl(observerCallback);
    return mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,

        attributes: false,
        characterData: false,
        attributeOldValue: false,
        characterDataOldValue: false,
    });
}

export const observer = {
    _observer: null,

    start() {
        if (!this._observer) {
            this._observer = startObserver();
        }
    },

    stop() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    },
};
